import styled from "@emotion/styled/macro";
import { css } from "@emotion/react";

/* ─── Theme: Deep Indigo for Labour Ward ─── */
const accent = "#1a237e";
const accentDark = "#0d1347";
const accentLight = "#e8eaf6";
const accentMid = "#3949ab";

/* ─── Page Wrapper ─── */
export const LWPageWrapper = styled.div`
    padding: 0 20px; width: 100%;
    @media (max-width: 768px) { padding: 0 12px; }
`;

/* ─── Header ─── */
export const LWHeader = styled.div`
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 20px;
    background: linear-gradient(135deg, ${accent} 0%, ${accentDark} 100%);
    color: #fff; border-radius: 10px; margin-bottom: 20px;
    flex-wrap: wrap; gap: 12px;
    box-shadow: 0 4px 12px rgba(26,35,126,0.25);
    @media (max-width: 768px) { flex-direction: column; align-items: flex-start; padding: 14px 16px; }
`;

export const LWHeaderTitle = styled.div`
    h3 { margin: 0 0 4px; font-size: 1.15rem; font-weight: 600; }
    p { margin: 0; font-size: 0.82rem; opacity: 0.85; }
`;

export const LWHeaderStats = styled.div`
    display: flex; gap: 10px; flex-wrap: wrap;
`;

export const StatBadge = styled.div`
    display: flex; align-items: center; gap: 5px;
    padding: 5px 12px; background: rgba(255,255,255,0.15);
    border-radius: 18px; font-size: 0.78rem; font-weight: 500;
    span.count { font-weight: 700; font-size: 0.9rem; }
`;

/* ─── Tab Bar ─── */
export const TabBar = styled.div`
    display: flex; gap: 4px; margin-bottom: 20px;
    background: #f0f2f5; border-radius: 10px; padding: 4px;
`;

export const TabBtn = styled.button`
    padding: 10px 18px; border: none; border-radius: 8px;
    font-size: 0.82rem; font-weight: 600; cursor: pointer; transition: all 0.2s;
    display: flex; align-items: center; gap: 6px; flex: 1; justify-content: center;
    background: ${function(p) { return p.active ? accent : "transparent"; }};
    color: ${function(p) { return p.active ? "#fff" : "#546e7a"; }};
    &:hover { background: ${function(p) { return p.active ? accent : "#e0e0e0"; }}; }
    .tab-count {
        font-size: 0.72rem; font-weight: 700; padding: 2px 8px;
        border-radius: 10px;
        background: ${function(p) { return p.active ? "rgba(255,255,255,0.2)" : "#cfd8dc"; }};
        color: ${function(p) { return p.active ? "#fff" : "#263238"; }};
    }
`;

/* ─── Alert Banner ─── */
export const AlertBanner = styled.div`
    padding: 10px 16px; border-radius: 8px; margin-bottom: 12px;
    display: flex; align-items: center; gap: 10px;
    ${function(p) {
        if (p.type === "critical") return css`
            background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
            border-left: 4px solid #e53935;
        `;
        return css`
            background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
            border-left: 4px solid #ff9800;
        `;
    }}
    .alert-icon { font-size: 1.2rem; flex-shrink: 0; }
    .alert-msg { font-size: 0.82rem; font-weight: 600;
        color: ${function(p) { return p.type === "critical" ? "#b71c1c" : "#e65100"; }};
    }
`;

/* ─── Section Nav ─── */
export const SectionNav = styled.div`
    display: flex; gap: 4px; background: #f0f2f5; border-radius: 10px;
    padding: 4px; margin-bottom: 20px; overflow-x: auto;
`;

export const SectionBtn = styled.button`
    padding: 10px 14px; border: none; border-radius: 8px;
    font-size: 0.78rem; font-weight: 600; cursor: pointer; transition: all 0.2s;
    display: flex; align-items: center; gap: 5px; white-space: nowrap; flex-shrink: 0;
    background: ${function(p) { return p.active ? accentMid : "transparent"; }};
    color: ${function(p) { return p.active ? "#fff" : "#546e7a"; }};
    &:hover { background: ${function(p) { return p.active ? accentMid : "#e0e0e0"; }}; }
    .nav-dot {
        width: 8px; height: 8px; border-radius: 50%;
        background: ${function(p) {
            if (p.active) return "rgba(255,255,255,0.6)";
            if (p.done) return "#43a047";
            return "#cfd8dc";
        }};
    }
`;

/* ═══ ANC Summary Strip ═══ */
export const ANCStrip = styled.div`
    display: flex; align-items: center; gap: 16px; padding: 12px 16px;
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    border: 1px solid #90caf9; border-radius: 8px; margin-bottom: 16px;
    flex-wrap: wrap;
    .anc-label { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.5px;
        color: #1565c0; font-weight: 700; }
    .anc-item {
        display: flex; flex-direction: column; align-items: center;
        font-size: 0.76rem; color: #263238;
        span.val { font-weight: 700; font-size: 0.88rem; color: #1565c0; }
        span.lbl { font-size: 0.66rem; color: #78909c; text-transform: uppercase; }
    }
    @media (max-width: 768px) { gap: 10px; padding: 10px 12px; }
`;

/* ═══ Form Sections ═══ */
export const FormSection = styled.div`
    background: #fff; border-radius: 10px; border: 1px solid #e8edf2;
    margin-bottom: 20px; overflow: hidden;
    box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`;

export const FormSectionHeader = styled.div`
    padding: 14px 20px; border-bottom: 1px solid #f0f2f5;
    display: flex; align-items: center; justify-content: space-between;
    background: ${function(p) { return p.bg || accentLight; }};
    h4 { margin: 0; font-size: 0.92rem; font-weight: 600;
        color: ${function(p) { return p.color || accent; }}; }
    .sh-badge { font-size: 0.72rem; font-weight: 600; padding: 3px 10px;
        border-radius: 10px; background: rgba(0,0,0,0.06); }
`;

export const FormSectionBody = styled.div` padding: 20px; `;

export const FieldGrid = styled.div`
    display: grid; grid-template-columns: repeat(${function(p) { return p.cols || 2; }}, 1fr);
    gap: 16px;
    @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

export const FormField = styled.div`
    label { display: block; font-size: 0.75rem; font-weight: 600; color: #546e7a;
        text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 6px; }
    input, select, textarea {
        width: 100%; padding: 10px 12px; border: 1px solid #cfd8dc;
        border-radius: 6px; font-size: 0.86rem; color: #263238; outline: none;
        transition: border 0.2s; box-sizing: border-box;
        &:focus { border-color: ${accent}; box-shadow: 0 0 0 2px rgba(26,35,126,0.1); }
    }
    textarea { resize: vertical; min-height: 60px; }
    .hint { font-size: 0.7rem; color: #90a4ae; margin-top: 3px; }
    .warn { font-size: 0.72rem; color: #e53935; font-weight: 600; margin-top: 3px; }
`;

/* ═══ Risk Assessment ═══ */
export const RiskContainer = styled.div`
    display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 10px;
    @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

export const RiskCard = styled.div`
    display: flex; align-items: center; gap: 10px;
    padding: 10px 14px; border-radius: 8px; cursor: pointer; transition: all 0.2s;
    border: 1.5px solid ${function(p) {
        if (p.checked) {
            if (p.severity === "critical") return "#e53935";
            if (p.severity === "high") return "#ff9800";
            return "#ffc107";
        }
        return "#e8edf2";
    }};
    background: ${function(p) {
        if (p.checked) {
            if (p.severity === "critical") return "#ffebee";
            if (p.severity === "high") return "#fff3e0";
            return "#fffde7";
        }
        return "#fafafa";
    }};
    &:hover { border-color: ${function(p) {
        if (p.severity === "critical") return "#e53935";
        if (p.severity === "high") return "#ff9800";
        return "#ffc107";
    }}; }
    .risk-check {
        width: 22px; height: 22px; border-radius: 4px; flex-shrink: 0;
        display: flex; align-items: center; justify-content: center;
        border: 2px solid ${function(p) {
            if (p.checked) {
                if (p.severity === "critical") return "#e53935";
                if (p.severity === "high") return "#ff9800";
                return "#ffc107";
            }
            return "#cfd8dc";
        }};
        background: ${function(p) {
            if (p.checked) {
                if (p.severity === "critical") return "#e53935";
                if (p.severity === "high") return "#ff9800";
                return "#ffc107";
            }
            return "#fff";
        }};
        color: #fff; font-size: 0.72rem; font-weight: 700;
    }
    .risk-label { font-size: 0.82rem; font-weight: 500; color: #263238; }
    .risk-severity {
        margin-left: auto; font-size: 0.66rem; font-weight: 700; text-transform: uppercase;
        letter-spacing: 0.4px; padding: 2px 8px; border-radius: 4px;
        color: ${function(p) {
            if (p.severity === "critical") return "#b71c1c";
            if (p.severity === "high") return "#e65100";
            return "#f57f17";
        }};
        background: ${function(p) {
            if (p.severity === "critical") return "#ffcdd2";
            if (p.severity === "high") return "#ffe0b2";
            return "#fff9c4";
        }};
    }
`;

export const RiskLevelBadge = styled.div`
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 16px; border-radius: 20px; font-size: 0.84rem; font-weight: 700;
    ${function(p) {
        if (p.level === "critical") return css`background: #ffcdd2; color: #b71c1c;`;
        if (p.level === "high") return css`background: #ffe0b2; color: #e65100;`;
        if (p.level === "medium") return css`background: #fff9c4; color: #f57f17;`;
        return css`background: #e8f5e9; color: #2e7d32;`;
    }}
`;

/* ═══ Board View ═══ */
export const BoardContainer = styled.div`
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px;
    @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

export const BoardColumn = styled.div`
    background: #f8f9fa; border-radius: 10px; min-height: 200px;
    overflow: hidden;
`;

export const BoardColumnHeader = styled.div`
    padding: 12px 16px; font-size: 0.82rem; font-weight: 700;
    display: flex; align-items: center; justify-content: space-between;
    color: #fff;
    ${function(p) {
        if (p.variant === "admitted") return css`background: ${accentMid};`;
        if (p.variant === "in_labour") return css`background: #e65100;`;
        return css`background: #2e7d32;`;
    }}
    .col-count {
        font-size: 0.72rem; padding: 2px 10px; border-radius: 10px;
        background: rgba(255,255,255,0.2);
    }
`;

export const BoardColumnBody = styled.div`
    padding: 10px; display: flex; flex-direction: column; gap: 8px;
    max-height: 60vh; overflow-y: auto;
    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-thumb { background: #bdbdbd; border-radius: 4px; }
`;

export const PatientCard = styled.div`
    background: #fff; border-radius: 8px; padding: 12px; cursor: pointer;
    border: 1px solid #e8edf2; transition: all 0.2s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
    &:hover { border-color: ${accentMid}; box-shadow: 0 2px 8px rgba(26,35,126,0.1); }
    .pc-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
    .pc-name { font-size: 0.88rem; font-weight: 700; color: #263238; }
    .pc-time { font-size: 0.68rem; color: #90a4ae; }
    .pc-meta { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 6px; }
    .pc-tag {
        font-size: 0.7rem; font-weight: 600; padding: 2px 8px;
        border-radius: 4px; background: #f5f5f5; color: #546e7a;
    }
    .pc-risk {
        font-size: 0.66rem; font-weight: 700; padding: 2px 8px;
        border-radius: 4px; text-transform: uppercase;
    }
    .pc-risk.critical { background: #ffcdd2; color: #b71c1c; }
    .pc-risk.high { background: #ffe0b2; color: #e65100; }
    .pc-risk.medium { background: #fff9c4; color: #f57f17; }
    .pc-risk.low { background: #e8f5e9; color: #2e7d32; }
`;

export const EmptyBoard = styled.div`
    padding: 30px 16px; text-align: center; color: #b0bec5;
    font-size: 0.82rem; font-style: italic;
`;

/* ─── Save Button ─── */
export const SaveBtn = styled.button`
    padding: 12px 32px; border-radius: 8px; border: none;
    background: ${accent}; color: #fff; font-size: 0.9rem; font-weight: 600;
    cursor: pointer; transition: all 0.2s;
    &:hover { background: ${accentDark}; }
    &:disabled { background: #9fa8da; cursor: not-allowed; }
`;

/* ─── Empty / Loading ─── */
export const EmptyState = styled.div`
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; padding: 60px 20px; text-align: center; color: #90a4ae;
    .empty-icon { font-size: 3rem; margin-bottom: 16px; }
    .empty-title { font-size: 1.05rem; font-weight: 600; color: #546e7a; margin-bottom: 8px; }
    .empty-desc { font-size: 0.85rem; max-width: 380px; line-height: 1.5; }
`;

export const LoadingWrapper = styled.div`
    display: flex; align-items: center; justify-content: center;
    padding: 60px 20px; color: #90a4ae; font-size: 0.9rem; gap: 10px;
`;

export const GoToBookingBtn = styled.button`
    margin-top: 20px; padding: 10px 24px; background: ${accent}; color: #fff;
    border: none; border-radius: 8px; font-size: 0.9rem; font-weight: 600;
    cursor: pointer; transition: background 0.2s;
    &:hover { background: ${accentDark}; }
`;

/* ─── Unbooked Info Banner ─── */
export const UnbookedBanner = styled.div`
    padding: 12px 16px; border-radius: 8px; margin-bottom: 16px;
    background: #fff3e0; border: 1px solid #ffcc80; color: #e65100;
    display: flex; align-items: center; gap: 10px;
    .ub-icon { font-size: 1.2rem; }
    .ub-text { font-size: 0.84rem; font-weight: 600; }
    .ub-link {
        margin-left: auto; padding: 6px 14px; border-radius: 6px; border: none;
        background: #e65100; color: #fff; font-size: 0.78rem; font-weight: 600;
        cursor: pointer; white-space: nowrap;
        &:hover { background: #bf360c; }
    }
`;
